import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Select,
  MenuItem,
  Chip,
  FormControl,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

export interface TimeRange {
  start: string;
  end: string;
}

export interface AvailabilitySlot {
  day: string;
  timeRanges: TimeRange[];
}

interface AvailabilitySlotEditorProps {
  slots: AvailabilitySlot[];
  onChange: (slots: AvailabilitySlot[]) => void;
  readOnly?: boolean;
  compact?: boolean;
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Generate time options in 30-minute intervals from 06:00 to 22:00
const TIME_OPTIONS: string[] = [];
for (let h = 6; h <= 22; h++) {
  TIME_OPTIONS.push(`${h.toString().padStart(2, '0')}:00`);
  if (h < 22) {
    TIME_OPTIONS.push(`${h.toString().padStart(2, '0')}:30`);
  }
}

const selectSx = {
  color: 'rgba(255, 255, 255, 0.9)',
  '.MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.4)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(100, 181, 246, 0.6)',
  },
  '.MuiSvgIcon-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
};

const menuPropsSx = {
  PaperProps: {
    sx: {
      backgroundColor: 'rgba(30, 41, 59, 0.98)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      maxHeight: 200,
      '& .MuiMenuItem-root': {
        color: 'rgba(255, 255, 255, 0.9)',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(100, 181, 246, 0.2)',
        },
      },
    },
  },
};

const AvailabilitySlotEditor: React.FC<AvailabilitySlotEditorProps> = ({
  slots,
  onChange,
  readOnly = false,
  compact = false,
}) => {
  const usedDays = slots.map(s => s.day);
  const availableDays = DAYS.filter(d => !usedDays.includes(d));

  const handleAddDay = () => {
    if (availableDays.length === 0) return;
    onChange([
      ...slots,
      { day: availableDays[0], timeRanges: [{ start: '09:00', end: '17:00' }] },
    ]);
  };

  const handleRemoveDay = (dayIndex: number) => {
    onChange(slots.filter((_, i) => i !== dayIndex));
  };

  const handleDayChange = (dayIndex: number, newDay: string) => {
    const updated = [...slots];
    updated[dayIndex] = { ...updated[dayIndex], day: newDay };
    onChange(updated);
  };

  const handleAddTimeRange = (dayIndex: number) => {
    const updated = [...slots];
    const lastRange = updated[dayIndex].timeRanges[updated[dayIndex].timeRanges.length - 1];
    const newStart = lastRange ? lastRange.end : '09:00';
    const newEnd = newStart < '20:00' ? 
      TIME_OPTIONS[Math.min(TIME_OPTIONS.indexOf(newStart) + 4, TIME_OPTIONS.length - 1)] : 
      '22:00';
    updated[dayIndex] = {
      ...updated[dayIndex],
      timeRanges: [...updated[dayIndex].timeRanges, { start: newStart, end: newEnd }],
    };
    onChange(updated);
  };

  const handleRemoveTimeRange = (dayIndex: number, rangeIndex: number) => {
    const updated = [...slots];
    updated[dayIndex] = {
      ...updated[dayIndex],
      timeRanges: updated[dayIndex].timeRanges.filter((_, i) => i !== rangeIndex),
    };
    // Remove the day entirely if no time ranges left
    if (updated[dayIndex].timeRanges.length === 0) {
      onChange(updated.filter((_, i) => i !== dayIndex));
    } else {
      onChange(updated);
    }
  };

  const handleTimeChange = (
    dayIndex: number,
    rangeIndex: number,
    field: 'start' | 'end',
    value: string
  ) => {
    const updated = [...slots];
    const ranges = [...updated[dayIndex].timeRanges];
    ranges[rangeIndex] = { ...ranges[rangeIndex], [field]: value };
    updated[dayIndex] = { ...updated[dayIndex], timeRanges: ranges };
    onChange(updated);
  };

  // Sort slots by day order
  const sortedSlots = [...slots].sort(
    (a, b) => DAYS.indexOf(a.day) - DAYS.indexOf(b.day)
  );

  // Read-only display
  if (readOnly) {
    if (slots.length === 0) {
      return (
        <Typography variant="body2" className="theme-text-muted">
          No specific time slots set
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {sortedSlots.map((slot) => (
          <Box key={slot.day} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={compact ? slot.day.substring(0, 3) : slot.day}
              size="small"
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                color: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid rgba(76, 175, 80, 0.4)',
                fontWeight: 600,
                minWidth: compact ? 40 : 90,
              }}
            />
            {slot.timeRanges.map((range, idx) => (
              <Chip
                key={idx}
                icon={<AccessTimeIcon sx={{ color: 'rgba(100, 181, 246, 0.8) !important', fontSize: 14 }} />}
                label={`${range.start} - ${range.end}`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(100, 181, 246, 0.15)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid rgba(100, 181, 246, 0.3)',
                  fontSize: '0.75rem',
                }}
              />
            ))}
          </Box>
        ))}
      </Box>
    );
  }

  // Editable display
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sortedSlots.map((slot, dayIndex) => {
        const actualIndex = slots.findIndex(s => s.day === slot.day);
        return (
          <Box
            key={slot.day}
            sx={{
              p: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={slot.day}
                  onChange={(e) => handleDayChange(actualIndex, e.target.value)}
                  sx={{ ...selectSx, fontWeight: 600 }}
                  MenuProps={menuPropsSx}
                >
                  <MenuItem value={slot.day}>{slot.day}</MenuItem>
                  {availableDays.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => handleAddTimeRange(actualIndex)}
                  sx={{ color: 'rgba(76, 175, 80, 0.9)', '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' } }}
                  title="Add time range"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveDay(actualIndex)}
                  sx={{ color: 'rgba(239, 68, 68, 0.8)', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' } }}
                  title="Remove day"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {slot.timeRanges.map((range, rangeIndex) => (
              <Box
                key={rangeIndex}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: rangeIndex < slot.timeRanges.length - 1 ? 1 : 0,
                  pl: 1,
                }}
              >
                <AccessTimeIcon sx={{ color: 'rgba(100, 181, 246, 0.7)', fontSize: 18 }} />
                <FormControl size="small" sx={{ minWidth: 95 }}>
                  <Select
                    value={range.start}
                    onChange={(e) => handleTimeChange(actualIndex, rangeIndex, 'start', e.target.value)}
                    sx={selectSx}
                    MenuProps={menuPropsSx}
                  >
                    {TIME_OPTIONS.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography className="theme-text-muted" sx={{ mx: 0.5 }}>to</Typography>
                <FormControl size="small" sx={{ minWidth: 95 }}>
                  <Select
                    value={range.end}
                    onChange={(e) => handleTimeChange(actualIndex, rangeIndex, 'end', e.target.value)}
                    sx={selectSx}
                    MenuProps={menuPropsSx}
                  >
                    {TIME_OPTIONS.filter((t) => t > range.start).map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {slot.timeRanges.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveTimeRange(actualIndex, rangeIndex)}
                    sx={{ color: 'rgba(239, 68, 68, 0.7)', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' } }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        );
      })}

      {availableDays.length > 0 && (
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddDay}
          sx={{
            textTransform: 'none',
            color: 'rgba(100, 181, 246, 0.9)',
            borderColor: 'rgba(100, 181, 246, 0.3)',
            '&:hover': {
              borderColor: 'rgba(100, 181, 246, 0.5)',
              backgroundColor: 'rgba(100, 181, 246, 0.08)',
            },
          }}
          variant="outlined"
          size="small"
        >
          Add Day
        </Button>
      )}
    </Box>
  );
};

export default AvailabilitySlotEditor;
